import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, message, Space, Row, Col,
  Typography, Popconfirm, Tag, Card, Statistic, Drawer, Upload, Divider, Tooltip, Alert, Empty, Badge, List, Descriptions, Select
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, UserAddOutlined, UploadOutlined,
  DollarOutlined, EyeOutlined, FileExcelOutlined,
  TeamOutlined, WalletOutlined, CheckCircleOutlined, ClockCircleOutlined, YoutubeOutlined,
  MessageOutlined, SendOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text, Paragraph } = Typography;

const clientCode = (id) => `CL-${id}`;
const fmtInr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtUsd = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
// Applied to the card BODY (the global .ant-card !important white bg overrides the root).
const cardBody = (grad) => ({ background: grad, borderRadius: 14 });

// Dependency-free horizontal balance bars (per client).
function BalanceBars({ clients }) {
  const data = (clients || [])
    .map((c) => ({ name: c.name, code: clientCode(c.id), bal: Number(c.summary?.balance_inr || 0), paid: Number(c.summary?.paid_inr || 0) }))
    .filter((d) => d.bal > 0 || d.paid > 0)
    .sort((a, b) => b.bal - a.bal)
    .slice(0, 8);
  if (!data.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No client balances yet" />;
  const max = Math.max(1, ...data.map((d) => d.bal + d.paid));
  return (
    <Space direction="vertical" style={{ width: '100%' }} size={14}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <Text>{d.code} · {d.name}</Text>
            <Text type="secondary">paid {fmtInr(d.paid)} · due <Text strong style={{ color: d.bal > 0 ? '#cf1322' : '#3f8600' }}>{fmtInr(d.bal)}</Text></Text>
          </div>
          <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: '#f0f0f5' }}>
            <div style={{ width: `${(d.paid / max) * 100}%`, background: '#52c41a' }} />
            <div style={{ width: `${(d.bal / max) * 100}%`, background: '#ff4d4f' }} />
          </div>
        </div>
      ))}
      <Space size={20} style={{ fontSize: 12, marginTop: 4 }}>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#52c41a', borderRadius: 3, marginRight: 6, verticalAlign: 'middle' }} />Paid</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#ff4d4f', borderRadius: 3, marginRight: 6, verticalAlign: 'middle' }} />Balance due</span>
      </Space>
    </Space>
  );
}

// Client-side CSV download (statement / ledger export).
function downloadRows(filename, rows) {
  if (!rows.length) { message.info('Nothing to export'); return; }
  const headers = Object.keys(rows[0]);
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function ClientPayments() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [clientModal, setClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm] = Form.useForm();

  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Detail drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // sub-forms
  const [channelModal, setChannelModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelForm] = Form.useForm();
  const [revenueModal, setRevenueModal] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState(null);
  const [revenueForm] = Form.useForm();
  const [paymentModal, setPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm] = Form.useForm();

  // Date-wise advance / payment ledger (shared with Client Channels hisab)
  const [hisabPays, setHisabPays] = useState([]);
  const [hisabModal, setHisabModal] = useState(false);
  const [hisabForm] = Form.useForm();

  // support / messages reply
  const [replyBody, setReplyBody] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    setLoading(true);
    try { const res = await api.get('/clients'); setClients(res.data.clients || []); }
    catch (err) { message.error(err.uiMessage || 'Failed to load clients'); }
    setLoading(false);
  };

  const openDetail = async (clientId) => {
    setDrawerOpen(true);
    setDetailLoading(true);
    try {
      const res = await api.get(`/clients/${clientId}`);
      setDetail(res.data);
      loadHisabPays(res.data?.client?.name);
    }
    catch (err) { message.error(err.uiMessage || 'Failed to load client'); }
    setDetailLoading(false);
  };

  const loadHisabPays = async (clientName) => {
    if (!clientName) { setHisabPays([]); return; }
    try {
      const res = await api.get('/client-channels/hisab');
      setHisabPays((res.data.payments || []).filter(p => p.client_name === clientName));
    } catch (err) { setHisabPays([]); }
  };

  const submitHisabPay = async (v) => {
    try {
      await api.post('/client-channels/hisab-payment', {
        client_name: detail.client.name,
        direction: v.direction,
        pay_date: v.pay_date || '',
        month: v.month || '',
        amount_inr: v.amount_inr || 0,
        amount_usd: v.amount_usd || 0,
        note: v.note || '',
      });
      message.success('Entry save ho gayi');
      setHisabModal(false); hisabForm.resetFields();
      loadHisabPays(detail.client.name);
    } catch (err) { message.error(err.response?.data?.error || err.uiMessage || 'Save failed'); }
  };

  const deleteHisabPay = async (id) => {
    try {
      await api.delete(`/client-channels/hisab-payment/${id}`);
      message.success('Entry hata di');
      loadHisabPays(detail.client.name);
    } catch (err) { message.error(err.uiMessage || 'Delete failed'); }
  };
  const refreshDetail = async () => { if (detail?.client?.id) await openDetail(detail.client.id); fetchClients(); };

  // Full per-client statement (info + channels + revenue + payments) as one CSV
  // so admin can download and send each client their own sheet.
  const downloadClientStatement = () => {
    if (!detail?.client) return;
    const c = detail.client;
    const su = detail.summary || {};
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const line = (arr) => arr.map(esc).join(',');
    const L = [];
    L.push(line([`Client Statement — ${c.name} (${clientCode(c.id)})`]));
    L.push(line(['Email', c.email || '', 'Phone', c.phone || '']));
    L.push(line(['Account Number', c.account_number || '', 'Address', c.address || '']));
    L.push('');
    L.push(line(['CHANNELS']));
    L.push(line(['Channel', 'URL', 'Client Share %']));
    (detail.channels || []).forEach((ch) => L.push(line([ch.channel_name, ch.url || '', ch.client_share_percent])));
    L.push('');
    L.push(line(['REVENUE (per channel / month)']));
    L.push(line(['Channel', 'Month', 'Revenue ($)', 'Client Share ($)']));
    (detail.revenue || []).forEach((r) => L.push(line([r.channel_name, r.month, r.revenue_usd, r.client_share_usd])));
    L.push('');
    L.push(line(['PAYMENTS MADE']));
    L.push(line(['Date', 'Month', 'Paid (₹)', 'Paid ($)', 'Note']));
    (detail.payments || []).forEach((p) => L.push(line([p.paid_on || '', p.month || '', p.amount_inr, p.amount_usd, p.note || ''])));
    L.push('');
    L.push(line(['SUMMARY']));
    L.push(line(['Total Revenue ($)', su.revenue_usd || 0]));
    L.push(line(['Client Share ($)', su.client_share_usd || 0]));
    L.push(line(['Client Share (₹)', su.client_share_inr || 0]));
    L.push(line(['Paid (₹)', su.paid_inr || 0]));
    L.push(line(['Balance / Pending (₹)', su.balance_inr || 0]));
    const blob = new Blob(['\ufeff' + L.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${clientCode(c.id)}-${c.name}-statement.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // ---- Client CRUD ----
  const submitClient = async (v) => {
    try {
      if (editingClient) await api.put(`/clients/${editingClient.id}`, v);
      else await api.post('/clients', v);
      message.success(editingClient ? 'Client updated' : 'Client created');
      setClientModal(false); clientForm.resetFields(); setEditingClient(null);
      fetchClients();
      window.dispatchEvent(new Event('clients-updated'));
    } catch (err) { message.error(err.uiMessage || 'Failed'); }
  };
  const deleteClient = async (id) => {
    try { await api.delete(`/clients/${id}`); message.success('Client deleted'); fetchClients(); window.dispatchEvent(new Event('clients-updated')); }
    catch (err) { message.error(err.uiMessage || 'Failed'); }
  };

  // ---- Import ----
  const doImport = async () => {
    if (!importFile) { message.warning('Choose a file first'); return; }
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await api.post('/clients/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      message.success(`Imported ${res.data.imported} client rows`);
      fetchClients();
      window.dispatchEvent(new Event('clients-updated'));
      if (detail?.client?.id) openDetail(detail.client.id);
    } catch (err) { message.error(err.uiMessage || 'Import failed'); }
    setImporting(false);
  };

  // ---- Channel ----
  const submitChannel = async (v) => {
    try {
      if (editingChannel) await api.put(`/clients/channels/${editingChannel.id}`, v);
      else await api.post(`/clients/${detail.client.id}/channels`, v);
      message.success(editingChannel ? 'Channel updated' : 'Channel added');
      setChannelModal(false); channelForm.resetFields(); setEditingChannel(null);
      refreshDetail();
    } catch (err) { message.error(err.uiMessage || 'Failed'); }
  };
  const deleteChannel = async (id) => {
    try { await api.delete(`/clients/channels/${id}`); message.success('Removed'); refreshDetail(); }
    catch (err) { message.error(err.uiMessage || 'Failed'); }
  };

  // ---- Revenue ----
  const submitRevenue = async (v) => {
    try {
      if (editingRevenue) await api.put(`/clients/revenue/${editingRevenue.id}`, v);
      else await api.post(`/clients/${detail.client.id}/revenue`, v);
      message.success(editingRevenue ? 'Revenue updated' : 'Revenue added');
      setRevenueModal(false); revenueForm.resetFields(); setEditingRevenue(null);
      refreshDetail();
    } catch (err) { message.error(err.uiMessage || 'Failed'); }
  };
  const deleteRevenue = async (id) => {
    try { await api.delete(`/clients/revenue/${id}`); message.success('Deleted'); refreshDetail(); }
    catch (err) { message.error(err.uiMessage || 'Failed'); }
  };

  // ---- Payment ----
  const submitPayment = async (v) => {
    try {
      if (editingPayment) await api.put(`/clients/payments/${editingPayment.id}`, v);
      else await api.post(`/clients/${detail.client.id}/payments`, v);
      message.success(editingPayment ? 'Payment updated' : 'Payment recorded');
      setPaymentModal(false); paymentForm.resetFields(); setEditingPayment(null);
      refreshDetail();
    } catch (err) { message.error(err.uiMessage || 'Failed'); }
  };
  const deletePayment = async (id) => {
    try { await api.delete(`/clients/payments/${id}`); message.success('Deleted'); refreshDetail(); }
    catch (err) { message.error(err.uiMessage || 'Failed'); }
  };

  // ---- Support / Messages ----
  const sendReply = async () => {
    if (!replyBody.trim()) { message.warning('Type a reply'); return; }
    setSendingReply(true);
    try { await api.post(`/clients/${detail.client.id}/messages`, { body: replyBody.trim() }); setReplyBody(''); message.success('Reply sent'); refreshDetail(); }
    catch (err) { message.error(err.uiMessage || 'Failed'); }
    setSendingReply(false);
  };

  const clientColumns = [
    { title: 'Client ID', dataIndex: 'id', render: (id) => <Tag color="blue">{clientCode(id)}</Tag>, width: 100 },
    { title: 'Client', dataIndex: 'name', render: (n, r) => (
      <Space direction="vertical" size={0}>
        <Text strong>{n}</Text>
        {r.email && <Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text>}
      </Space>
    ) },
    { title: 'Channels', key: 'channels', width: 130, align: 'center', render: (_, r) => {
      const led = Number(r.ledger_channel_count || 0);
      const rev = Number(r.ledger_revenue_usd || 0);
      return (
        <Space direction="vertical" size={0}>
          <Tag color={led > 0 ? 'geekblue' : 'default'}>{led} channel{led === 1 ? '' : 's'}</Tag>
          {rev > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{fmtUsd(rev)}</Text>}
        </Space>
      );
    } },
    { title: 'Total Revenue', key: 'rev', render: (_, r) => (
      <Space direction="vertical" size={0}>
        <Text>{fmtUsd(r.summary.revenue_usd)}</Text>
      </Space>
    ) },
    { title: 'Client Share', key: 'share', render: (_, r) => (
      <Space direction="vertical" size={0}>
        <Text>{fmtUsd(r.summary.client_share_usd)}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>{fmtInr(r.summary.client_share_inr)}</Text>
      </Space>
    ) },
    { title: 'Revenue share % (hamara cut)', dataIndex: 'cms_cut_percent', width: 130, align: 'center', render: (p) => (
      Number(p) > 0 ? <Tag color="purple">{Number(p)}%</Tag> : <Text type="secondary">set nahi</Text>
    ) },
    { title: 'Client ko dena (cut ke baad)', key: 'payable', width: 160, render: (_, r) => {
      const gross = Number(r.summary.revenue_usd || 0);
      const cut = Number(r.cms_cut_percent || 0);
      return (
        <Space direction="vertical" size={0}>
          <Text strong>{fmtUsd(gross * (1 - cut / 100))}</Text>
          {cut > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{`cut ${cut}% = ${fmtUsd(gross * cut / 100)}`}</Text>}
        </Space>
      );
    } },
    { title: 'Advance (₹)', key: 'adv', width: 120, render: (_, r) => {
      const a = Number(r.summary?.advance_inr || r.advance_inr || 0);
      return a > 0 ? <Tag color="orange">{fmtInr(a)}</Tag> : <Text type="secondary">—</Text>;
    } },
    { title: 'Paid', key: 'paid', render: (_, r) => <Text>{fmtInr(r.summary.paid_inr)}</Text> },
    { title: 'Balance (₹)', key: 'bal', render: (_, r) => {
      const b = r.summary.balance_inr;
      return <Tag color={b > 0 ? 'red' : 'green'}>{fmtInr(b)}</Tag>;
    } },
    { title: 'Account No.', dataIndex: 'account_number', width: 140, render: (a) => a ? <Text copyable style={{ fontSize: 12 }}>{a}</Text> : <Text type="secondary">—</Text> },
    { title: 'Requests', key: 'msg', width: 100, align: 'center', render: (_, r) => (
      r.unread_from_client > 0
        ? <Badge count={r.unread_from_client}><Tag color="volcano" icon={<MessageOutlined />}>New</Tag></Badge>
        : <Text type="secondary">—</Text>
    ) },
    { title: 'Actions', key: 'act', width: 160, render: (_, r) => (
      <Space>
        <Tooltip title="Open ledger"><Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r.id)} /></Tooltip>
        <Tooltip title="Edit client"><Button size="small" icon={<EditOutlined />} onClick={() => { setEditingClient(r); clientForm.setFieldsValue({ name: r.name, email: r.email, phone: r.phone, address: r.address, account_number: r.account_number, cms_cut_percent: Number(r.cms_cut_percent || 0), advance_inr: Number(r.advance_inr || 0), notes: r.notes }); setClientModal(true); }} /></Tooltip>
        <Popconfirm title="Delete this client and all its data?" onConfirm={() => deleteClient(r.id)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ) },
  ];

  const s = detail?.summary;

  // Aggregate totals across all clients (top-level dashboard).
  const agg = clients.reduce((a, c) => {
    const su = c.summary || {};
    a.revenue_usd += Number(su.revenue_usd || 0);
    a.cut_usd += Number(su.cut_usd || 0);
    a.client_share_inr += Number(su.client_share_inr || 0);
    a.paid_inr += Number(su.paid_inr || 0);
    a.balance_inr += Number(su.balance_inr || 0);
    a.channels += Number(c.channel_count || 0);
    return a;
  }, { revenue_usd: 0, cut_usd: 0, client_share_inr: 0, paid_inr: 0, balance_inr: 0, channels: 0 });

  return (
    <div>
      {/* Dashboard banner */}
      <Card style={{ borderRadius: 16, marginBottom: 20, overflow: 'hidden' }} bodyStyle={{ padding: '20px 24px', background: 'linear-gradient(120deg,#1a1a2e 0%,#3a3a6a 100%)' }}>
        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col>
            <Title level={3} style={{ color: '#fff', margin: 0 }}><UserAddOutlined /> Client Payments</Title>
            <Text style={{ color: 'rgba(255,255,255,0.75)' }}>Client channels ka alag hisaab — Best Music (Bainsla) ke Income/Expense se bilkul alag.</Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<FileExcelOutlined />} onClick={() => { setImportModal(true); setImportResult(null); setImportFile(null); }}>Import Excel</Button>
              <Button type="primary" icon={<UserAddOutlined />} onClick={() => { setEditingClient(null); clientForm.resetFields(); setClientModal(true); }}>Add Client</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Aggregate summary cards */}
      <Row gutter={[16, 16]}>
        <Col xs={12} md={8} lg={4}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#667eea22,#667eea08)')}>
            <Statistic title={<Text strong>Clients</Text>} value={clients.length} prefix={<TeamOutlined style={{ color: '#667eea' }} />} valueStyle={{ color: '#5566d6', fontSize: 24 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#faad1422,#faad1408)')}>
            <Statistic title={<Text strong>Channels</Text>} value={agg.channels} prefix={<YoutubeOutlined style={{ color: '#d48806' }} />} valueStyle={{ color: '#d48806', fontSize: 24 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#13c2c222,#13c2c208)')}>
            <Statistic title={<Text strong>Total Revenue ($)</Text>} value={agg.revenue_usd} precision={2} formatter={(v) => fmtUsd(v)} valueStyle={{ color: '#08979c', fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#52c41a22,#52c41a08)')}>
            <Statistic title={<Text strong>Your Cut ($)</Text>} value={agg.cut_usd} precision={2} prefix={<DollarOutlined style={{ color: '#3f8600' }} />} formatter={(v) => fmtUsd(v)} valueStyle={{ color: '#3f8600', fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#52c41a22,#52c41a08)')}>
            <Statistic title={<Text strong>Paid (₹)</Text>} value={agg.paid_inr} precision={2} prefix={<CheckCircleOutlined style={{ color: '#3f8600' }} />} formatter={(v) => fmtInr(v)} valueStyle={{ color: '#3f8600', fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card bodyStyle={cardBody(agg.balance_inr > 0 ? 'linear-gradient(135deg,#ff4d4f22,#ff4d4f08)' : 'linear-gradient(135deg,#52c41a22,#52c41a08)')}>
            <Statistic title={<Text strong>Outstanding (₹)</Text>} value={agg.balance_inr} precision={2} prefix={agg.balance_inr > 0 ? <ClockCircleOutlined style={{ color: '#cf1322' }} /> : <CheckCircleOutlined style={{ color: '#3f8600' }} />} formatter={(v) => fmtInr(v)} valueStyle={{ color: agg.balance_inr > 0 ? '#cf1322' : '#3f8600', fontSize: 22 }} />
          </Card>
        </Col>
      </Row>

      {/* Balance chart + clients table */}
      <Card title="Paid vs Balance (top clients)" style={{ borderRadius: 14, marginTop: 16 }}>
        <BalanceBars clients={clients} />
      </Card>

      <Card title={<Space><WalletOutlined />All Clients</Space>} style={{ borderRadius: 14, marginTop: 16 }} bodyStyle={{ paddingTop: 8 }}>
        <Table rowKey="id" loading={loading} columns={clientColumns} dataSource={clients} pagination={false} scroll={{ x: 1500 }}
          locale={{ emptyText: 'No clients yet — click "Add Client" to start.' }} />
      </Card>

      {/* ===== Add/Edit Client ===== */}
      <Modal title={editingClient ? 'Edit Client' : 'Add Client'} open={clientModal} onCancel={() => setClientModal(false)} onOk={() => clientForm.submit()} okText="Save">
        <Form form={clientForm} layout="vertical" onFinish={submitClient}>
          <Form.Item name="name" label="Client / Company Name" rules={[{ required: true }]}><Input placeholder="e.g. Ajeet Music" /></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="email" label="Email"><Input placeholder="client@email.com" /></Form.Item></Col>
            <Col span={12}><Form.Item name="phone" label="Phone"><Input placeholder="98xxxxxxxx" /></Form.Item></Col>
          </Row>
          <Form.Item name="account_number" label="Account Number"><Input placeholder="Bank account / UPI (payment ke liye)" /></Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="cms_cut_percent" label="Revenue share % (hamara cut, 100% me se)" tooltip="Jo hum kaat rahe hain wahi daalo — jaise 30. Bacha hua 70% client ko payable.">
                <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} placeholder="e.g. 20" addonAfter="%" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="advance_inr" label="Purana advance (₹) — date-wise entries client ke andar">
                <InputNumber min={0} step={1000} style={{ width: '100%' }} placeholder="0" addonBefore="₹" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="address" label="Address"><Input.TextArea rows={2} placeholder="Client ka pura pata" /></Form.Item>
          <Form.Item name="notes" label="Notes (optional)"><Input.TextArea rows={2} /></Form.Item>
          {!editingClient && <Alert type="info" showIcon message="Sirf client ki detail store hoti hai — client ko koi login/access nahi milta. Save ke baad Client ID milega (CL-1, CL-2 …)." />}
        </Form>
      </Modal>

      {/* ===== Import Excel ===== */}
      <Modal title="Import Channel Earnings (Excel / CSV)" open={importModal} onCancel={() => setImportModal(false)} footer={null} width={620}>
        <Paragraph type="secondary">
          Columns: <b>Channel Name, URL, Revenue ($ total), Month, Share ($ client share), INR (₹ client share), Client ID</b>.
          Jis row me Client ID (CL-1 / 1) hoga wo us client ke ledger me jayegi. Bina Client ID wali (Bainsla apne) rows skip hongi — main hisaab safe rahega.
        </Paragraph>
        <Upload.Dragger
          beforeUpload={(f) => { setImportFile(f); return false; }}
          maxCount={1}
          accept=".csv,.xls,.xlsx"
          onRemove={() => setImportFile(null)}
          fileList={importFile ? [importFile] : []}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p>Click or drag Excel/CSV here</p>
        </Upload.Dragger>
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <Button type="primary" loading={importing} onClick={doImport} disabled={!importFile}>Upload & Import</Button>
        </div>
        {importResult && (
          <Alert
            style={{ marginTop: 16 }}
            type="success"
            showIcon
            message="Import complete"
            description={
              <div>
                <div>Client rows imported: <b>{importResult.imported}</b></div>
                <div>Bainsla / own rows skipped (no Client ID): <b>{importResult.own_rows}</b>
                  {importResult.own_channels?.length ? ` — ${importResult.own_channels.join(', ')}` : ''}</div>
                {importResult.unknown_client > 0 && <div style={{ color: '#cf1322' }}>Unknown Client ID rows skipped: <b>{importResult.unknown_client}</b></div>}
                {importResult.skipped > 0 && <div>Empty rows skipped: {importResult.skipped}</div>}
              </div>
            }
          />
        )}
      </Modal>

      {/* ===== Client Ledger Drawer ===== */}
      <Drawer
        open={drawerOpen}
        width={Math.min(1000, window.innerWidth - 40)}
        onClose={() => { setDrawerOpen(false); setDetail(null); }}
        title={detail ? <Space><Tag color="blue">{clientCode(detail.client.id)}</Tag>{detail.client.name}</Space> : 'Client'}
        extra={detail ? <Button type="primary" icon={<FileExcelOutlined />} onClick={downloadClientStatement}>Download Client Sheet</Button> : null}
        loading={detailLoading}
      >
        {detail && (
          <>
            <Card size="small" style={{ marginBottom: 12, borderRadius: 10 }}>
              <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item label="Email">{detail.client.email || '—'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{detail.client.phone || '—'}</Descriptions.Item>
                <Descriptions.Item label="Account Number">{detail.client.account_number ? <Text copyable>{detail.client.account_number}</Text> : '—'}</Descriptions.Item>
                <Descriptions.Item label="Address">{detail.client.address || '—'}</Descriptions.Item>
                <Descriptions.Item label="Revenue share % (hamara cut)">{Number(detail.client.cms_cut_percent || 0)}%</Descriptions.Item>
                <Descriptions.Item label="Purana advance (client record)">{fmtInr(detail.client.advance_inr || 0)}</Descriptions.Item>
              </Descriptions>
            </Card>
            <Row gutter={[12, 12]}>
              <Col span={8}><Card size="small" bodyStyle={cardBody('linear-gradient(135deg,#13c2c222,#13c2c208)')}><Statistic title="Total Revenue ($)" value={s.revenue_usd} precision={2} formatter={(v) => fmtUsd(v)} valueStyle={{ color: '#08979c' }} /></Card></Col>
              <Col span={8}><Card size="small" bodyStyle={cardBody('linear-gradient(135deg,#52c41a22,#52c41a08)')}><Statistic title={`Hamara cut ($) — ${Number(s.cut_percent || 0)}%`} value={s.cut_usd} precision={2} formatter={(v) => fmtUsd(v)} valueStyle={{ color: '#3f8600' }} /></Card></Col>
              <Col span={8}><Card size="small" bodyStyle={cardBody('linear-gradient(135deg,#667eea22,#667eea08)')}><Statistic title="Client ko dena ($) — cut ke baad" value={s.client_share_usd} precision={2} formatter={(v) => fmtUsd(v)} valueStyle={{ color: '#5566d6' }} /></Card></Col>
              <Col span={8}><Card size="small" bodyStyle={cardBody('linear-gradient(135deg,#667eea22,#667eea08)')}><Statistic title="Client ko dena (₹) — cut ke baad" value={s.client_share_inr} precision={2} formatter={(v) => fmtInr(v)} valueStyle={{ color: '#5566d6' }} /></Card></Col>
              <Col span={8}><Card size="small" bodyStyle={cardBody('linear-gradient(135deg,#52c41a22,#52c41a08)')}><Statistic title="Paid (₹)" value={s.paid_inr} precision={2} formatter={(v) => fmtInr(v)} valueStyle={{ color: '#3f8600' }} /></Card></Col>
              <Col span={8}><Card size="small" bodyStyle={cardBody(s.balance_inr > 0 ? 'linear-gradient(135deg,#ff4d4f22,#ff4d4f08)' : 'linear-gradient(135deg,#52c41a22,#52c41a08)')}><Statistic title="Balance (₹)" value={s.balance_inr} precision={2} formatter={(v) => fmtInr(v)} valueStyle={{ color: s.balance_inr > 0 ? '#cf1322' : '#3f8600' }} /></Card></Col>
            </Row>

            {/* Date-wise advance / payment ledger */}
            <Divider orientation="left" style={{ marginTop: 24 }}>Advance & Payment Hisab (date-wise)</Divider>
            {(() => {
              const t = hisabPays.reduce((a, p) => {
                const k = ['received', 'advance'].includes(p.direction) ? p.direction : 'paid';
                a[`${k}_inr`] += Number(p.amount_inr || 0);
                a[`${k}_usd`] += Number(p.amount_usd || 0);
                return a;
              }, { paid_inr: 0, paid_usd: 0, advance_inr: 0, advance_usd: 0, received_inr: 0, received_usd: 0 });
              const payableInr = Number(s.client_share_inr || 0);
              const staticAdvance = Number(s.advance_static_inr || 0);
              const advanceInr = t.advance_inr + staticAdvance;
              const baki = payableInr - advanceInr - t.paid_inr;
              return (
                <>
                  <Space wrap size="large" style={{ marginBottom: 8 }}>
                    <span><Text strong>Client ko dena (cut ke baad):</Text> <Tag color="blue">{fmtInr(payableInr)}</Tag></span>
                    <span><Text strong>Advance liya:</Text> <Tag color="orange">{fmtInr(advanceInr)}</Tag>{staticAdvance > 0 && <Text type="secondary" style={{ fontSize: 12 }}> (isme purana record ₹{staticAdvance.toLocaleString('en-IN')} bhi hai)</Text>}</span>
                    <span><Text strong>Paid:</Text> <Tag color="green">{fmtInr(t.paid_inr)}</Tag></span>
                    <span><Text strong>Received:</Text> <Tag color="cyan">{fmtInr(t.received_inr)}</Tag></span>
                    <span><Text strong>Baki (ab tak):</Text> <Tag color={baki > 0 ? 'red' : 'green'}>{fmtInr(baki)}</Tag></span>
                  </Space>
                  <div>
                    <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginBottom: 8 }}
                      onClick={() => { hisabForm.resetFields(); hisabForm.setFieldsValue({ direction: 'advance', pay_date: new Date().toISOString().slice(0, 10) }); setHisabModal(true); }}>
                      Advance / Payment add karo
                    </Button>
                  </div>
                  <Table size="small" rowKey="id" pagination={false} dataSource={hisabPays} scroll={{ x: 800 }}
                    locale={{ emptyText: 'Abhi koi advance/payment entry nahi — date ke saath yahan daalo.' }}
                    columns={[
                      { title: 'Date', dataIndex: 'pay_date', width: 110 },
                      { title: 'Kya hua', dataIndex: 'direction', width: 110, render: (d) => <Tag color={d === 'advance' ? 'orange' : d === 'received' ? 'cyan' : 'green'}>{d === 'advance' ? 'Advance' : d === 'received' ? 'Received' : 'Paid'}</Tag> },
                      { title: 'Month', dataIndex: 'month', width: 100, render: (m) => m || '—' },
                      { title: 'Amount (₹)', dataIndex: 'amount_inr', width: 130, align: 'right', render: (v) => fmtInr(v) },
                      { title: 'Amount ($)', dataIndex: 'amount_usd', width: 110, align: 'right', render: (v) => Number(v) ? fmtUsd(v) : '—' },
                      { title: 'Rate (₹/$)', dataIndex: 'usd_inr_rate', width: 100, align: 'right', render: (v) => Number(v) ? Number(v).toFixed(2) : '—' },
                      { title: 'Note', dataIndex: 'note' },
                      { title: '', width: 60, render: (_, r) => (
                        <Popconfirm title="Ye galat entry hata deni hai?" onConfirm={() => deleteHisabPay(r.id)}>
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      ) },
                    ]}
                  />
                </>
              );
            })()}

            {/* Channels */}
            <Divider orientation="left" style={{ marginTop: 24 }}>Channels</Divider>
            <Space style={{ marginBottom: 8 }}>
              <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => { setEditingChannel(null); channelForm.resetFields(); setChannelModal(true); }}>Add Channel</Button>
              <Button size="small" icon={<FileExcelOutlined />} onClick={() => downloadRows(`${clientCode(detail.client.id)}-channels.csv`, detail.channels.map((c) => ({ 'Channel Name': c.channel_name, URL: c.url || '', 'Client Share %': c.client_share_percent, 'Your Cut %': (100 - Number(c.client_share_percent)).toFixed(2) })))}>Download Channel List</Button>
            </Space>
            <Table
              size="small" rowKey="id" pagination={false}
              dataSource={detail.channels}
              columns={[
                { title: 'Channel', dataIndex: 'channel_name' },
                { title: 'URL', dataIndex: 'url', render: (u) => u ? <a href={u} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>{u}</a> : '-' },
                { title: 'Client Share %', dataIndex: 'client_share_percent', render: (p) => `${p}%` },
                { title: 'Your Cut %', dataIndex: 'client_share_percent', render: (p) => `${(100 - Number(p)).toFixed(2)}%` },
                { title: '', width: 80, render: (_, r) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingChannel(r); channelForm.setFieldsValue({ channel_name: r.channel_name, url: r.url, client_share_percent: r.client_share_percent }); setChannelModal(true); }} />
                    <Popconfirm title="Remove channel?" onConfirm={() => deleteChannel(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                  </Space>
                ) },
              ]}
            />

            {/* Revenue */}
            <Divider orientation="left" style={{ marginTop: 24 }}>Revenue (per channel / month)</Divider>
            <Space style={{ marginBottom: 8 }}>
              <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => { setEditingRevenue(null); revenueForm.resetFields(); setRevenueModal(true); }}>Add Revenue</Button>
              <Button size="small" icon={<FileExcelOutlined />} onClick={() => downloadRows(`${clientCode(detail.client.id)}-revenue.csv`, detail.revenue.map((r) => ({ Channel: r.channel_name, URL: r.url, Month: r.month, 'Revenue($)': r.revenue_usd, 'Share($)': r.client_share_usd, 'INR(₹)': r.client_share_inr })))}>Export</Button>
            </Space>
            <Table
              size="small" rowKey="id" pagination={false} scroll={{ x: 700 }}
              dataSource={detail.revenue}
              columns={[
                { title: 'Channel', dataIndex: 'channel_name' },
                { title: 'Month', dataIndex: 'month' },
                { title: 'Revenue ($)', dataIndex: 'revenue_usd', render: (v) => fmtUsd(v) },
                { title: 'Share ($)', dataIndex: 'client_share_usd', render: (v) => fmtUsd(v) },
                { title: 'INR (₹)', dataIndex: 'client_share_inr', render: (v) => fmtInr(v) },
                { title: '', width: 80, render: (_, r) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingRevenue(r); revenueForm.setFieldsValue({ channel_name: r.channel_name, url: r.url, month: r.month, revenue_usd: r.revenue_usd, client_share_usd: r.client_share_usd, client_share_inr: r.client_share_inr }); setRevenueModal(true); }} />
                    <Popconfirm title="Delete row?" onConfirm={() => deleteRevenue(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                  </Space>
                ) },
              ]}
            />

            {/* Payments */}
            <Divider orientation="left" style={{ marginTop: 24 }}>Payments Made</Divider>
            <Button size="small" type="dashed" icon={<DollarOutlined />} onClick={() => { setEditingPayment(null); paymentForm.resetFields(); setPaymentModal(true); }} style={{ marginBottom: 8 }}>Record Payment</Button>
            <Table
              size="small" rowKey="id" pagination={false}
              dataSource={detail.payments}
              columns={[
                { title: 'Date', dataIndex: 'paid_on' },
                { title: 'Month', dataIndex: 'month' },
                { title: 'Paid (₹)', dataIndex: 'amount_inr', render: (v) => fmtInr(v) },
                { title: 'Paid ($)', dataIndex: 'amount_usd', render: (v) => fmtUsd(v) },
                { title: 'Note', dataIndex: 'note' },
                { title: '', width: 80, render: (_, r) => (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingPayment(r); paymentForm.setFieldsValue({ amount_inr: r.amount_inr, amount_usd: r.amount_usd, month: r.month, note: r.note, paid_on: r.paid_on }); setPaymentModal(true); }} />
                    <Popconfirm title="Delete payment?" onConfirm={() => deletePayment(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                  </Space>
                ) },
              ]}
            />

            {/* Support / Messages */}
            <Divider orientation="left" style={{ marginTop: 24 }}>
              <Space><MessageOutlined />Client Requests / Support</Space>
            </Divider>
            {(!detail.messages || detail.messages.length === 0) ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No messages from this client yet" />
            ) : (
              <List
                dataSource={detail.messages}
                renderItem={(m) => {
                  const fromAdmin = m.sender === 'admin';
                  return (
                    <List.Item style={{ justifyContent: fromAdmin ? 'flex-end' : 'flex-start', border: 'none', padding: '6px 0' }}>
                      <div style={{
                        maxWidth: '78%',
                        background: fromAdmin ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#f0f0f5',
                        color: fromAdmin ? '#fff' : '#222',
                        padding: '10px 14px', borderRadius: 12,
                      }}>
                        <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>{fromAdmin ? 'You (Admin)' : detail.client.name} · {m.created_at}</div>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
            <div style={{ marginTop: 12 }}>
              <Input.TextArea rows={3} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Reply to client…" maxLength={1000} />
              <Button type="primary" icon={<SendOutlined />} loading={sendingReply} onClick={sendReply} style={{ marginTop: 10 }}>Send Reply</Button>
            </div>
          </>
        )}
      </Drawer>

      {/* Channel modal */}
      <Modal title={editingChannel ? 'Edit Channel' : 'Add Channel'} open={channelModal} onCancel={() => setChannelModal(false)} onOk={() => channelForm.submit()} okText="Save">
        <Form form={channelForm} layout="vertical" onFinish={submitChannel}>
          <Form.Item name="channel_name" label="Channel Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="url" label="Channel URL"><Input placeholder="https://youtube.com/@..." /></Form.Item>
          <Form.Item name="client_share_percent" label="Client Share %" extra="Client ka hissa (jaise 75). Aapka cut = 100 − ye." rules={[{ required: true }]}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} addonAfter="%" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Revenue modal */}
      <Modal title={editingRevenue ? 'Edit Revenue' : 'Add Revenue'} open={revenueModal} onCancel={() => setRevenueModal(false)} onOk={() => revenueForm.submit()} okText="Save">
        <Form form={revenueForm} layout="vertical" onFinish={submitRevenue}>
          <Form.Item name="channel_name" label="Channel Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="month" label="Month" rules={[{ required: true }]}><Input placeholder="e.g. May 2025" /></Form.Item>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="revenue_usd" label="Revenue ($ total)"><InputNumber min={0} style={{ width: '100%' }} prefix="$" /></Form.Item></Col>
            <Col span={8}><Form.Item name="client_share_usd" label="Share ($)"><InputNumber min={0} style={{ width: '100%' }} prefix="$" /></Form.Item></Col>
            <Col span={8}><Form.Item name="client_share_inr" label="INR (₹)"><InputNumber min={0} style={{ width: '100%' }} prefix="₹" /></Form.Item></Col>
          </Row>
          <Alert type="info" showIcon message="Share ($) = client ka hissa. INR (₹) = wahi rupaye me. Khaali chhodo to channel ke % se auto nikal jayega." />
        </Form>
      </Modal>

      {/* Payment modal */}
      <Modal title={editingPayment ? 'Edit Payment' : 'Record Payment'} open={paymentModal} onCancel={() => setPaymentModal(false)} onOk={() => paymentForm.submit()} okText="Save">
        <Form form={paymentForm} layout="vertical" onFinish={submitPayment}>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="amount_inr" label="Amount Paid (₹)"><InputNumber min={0} style={{ width: '100%' }} prefix="₹" /></Form.Item></Col>
            <Col span={12}><Form.Item name="amount_usd" label="Amount Paid ($)"><InputNumber min={0} style={{ width: '100%' }} prefix="$" /></Form.Item></Col>
          </Row>
          <Form.Item name="paid_on" label="Date"><Input type="date" /></Form.Item>
          <Form.Item name="month" label="For Month (optional)"><Input placeholder="e.g. May 2025" /></Form.Item>
          <Form.Item name="note" label="Note (optional)"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      {/* Advance / payment entry (date-wise, shared with Client Channels hisab) */}
      <Modal title={`Advance / Payment — ${detail?.client?.name || ''}`} open={hisabModal}
        onCancel={() => setHisabModal(false)} onOk={() => hisabForm.submit()} okText="Save">
        <Form form={hisabForm} layout="vertical" onFinish={submitHisabPay}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="direction" label="Kya hua" initialValue="advance" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'advance', label: 'Advance (client ne advance liya)' },
                  { value: 'paid', label: 'Paid (client ko diya)' },
                  { value: 'received', label: 'Received (hamein aaya)' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pay_date" label="Date (jo chaho wahi chuno — peeche ki bhi)" rules={[{ required: true, message: 'Date chuno' }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="amount_inr" label="Amount (₹)" rules={[{ required: true, message: 'Amount daalo' }]}>
            <InputNumber min={0} step={1000} style={{ width: '100%' }} prefix="₹" />
          </Form.Item>
          <Form.Item name="note" label="Note (optional)"><Input.TextArea rows={2} placeholder="e.g. 16 tarikh ka advance" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
